import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import i18n from '../../i18n/i18n';

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Helper function to get current language
const getCurrentLanguage = () => {
    return i18n.language || 'ar';
};

// Async thunks for store authentication
export const storeLogin = createAsyncThunk(
    'storeAuth/storeLogin',
    async (loginData, { rejectWithValue }) => {
        try {
            const currentLang = getCurrentLanguage();
        const response = await axios.post(`${BASE_URL}/stores/store_login`, {
            role: loginData.role || 'store',
            phone: loginData.phone,
            password: loginData.password
        }, {
                headers: {
                    "Accept-Language": currentLang,
                    "lang": currentLang,
                },
            });
            
            // التحقق من حالة الاستجابة
            if (response.data.status === 'faild' || response.data.status === 'failed') {
                return rejectWithValue(response.data.message || 'فشل تسجيل الدخول');
            }
            
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.response?.data || 'فشل تسجيل الدخول');
        }
    }
);

export const storeRegister = createAsyncThunk(
    'storeAuth/storeRegister',
    async (registerData, { rejectWithValue }) => {
        try {
            const currentLang = getCurrentLanguage();
            const formData = new FormData();
            Object.keys(registerData).forEach(key => {
                if (registerData[key] !== null && registerData[key] !== '') {
                    formData.append(key, registerData[key]);
                }
            });

            const response = await axios.post(`${BASE_URL}/stores/register`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    "Accept-Language": currentLang,
                    "lang": currentLang,
                },
            });
            
            // التحقق من حالة الاستجابة
            if (response.data.status === 'faild' || response.data.status === 'failed') {
                return rejectWithValue(response.data.message || 'فشل في إنشاء الحساب');
            }
            
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.response?.data || 'فشل في إنشاء الحساب');
        }
    }
);

export const storeReset = createAsyncThunk(
    'storeAuth/storeReset',
    async (phone, { rejectWithValue }) => {
        try {
            const currentLang = getCurrentLanguage();
            const response = await axios.post(`${BASE_URL}/stores/store_reset`, {
                phone: phone.startsWith('+') ? phone : `+2${phone}`
            }, {
                headers: {
                    "Accept-Language": currentLang,
                    "lang": currentLang,
                },
            });
            
            // التحقق من حالة الاستجابة
            if (response.data.status === 'faild' || response.data.status === 'failed') {
                return rejectWithValue(response.data.message || 'فشل في إرسال كود التحقق');
            }
            
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.response?.data || 'فشل في إرسال كود التحقق');
        }
    }
);

export const storeCheckCode = createAsyncThunk(
    'storeAuth/storeCheckCode',
    async (checkData, { rejectWithValue }) => {
        try {
            const currentLang = getCurrentLanguage();
            const response = await axios.post(`${BASE_URL}/stores/store_check_code`, {
                phone: checkData.phone.startsWith('+') ? checkData.phone : `+2${checkData.phone}`,
                code: checkData.code
            }, {
                headers: {
                    "Accept-Language": currentLang,
                    "lang": currentLang,
                },
            });
            
            // التحقق من حالة الاستجابة
            if (response.data.status === 'faild' || response.data.status === 'failed') {
                return rejectWithValue(response.data.message || 'الكود غير صحيح');
            }
            
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.response?.data || 'الكود غير صحيح');
        }
    }
);

export const storeChangePassword = createAsyncThunk(
    'storeAuth/storeChangePassword',
    async (changeData, { rejectWithValue }) => {
        try {
            const currentLang = getCurrentLanguage();
            const response = await axios.post(`${BASE_URL}/stores/store_change_password`, {
                phone: changeData.phone.startsWith('+') ? changeData.phone : `+2${changeData.phone}`,
                password: changeData.password
            }, {
                headers: {
                    "Accept-Language": currentLang,
                    "lang": currentLang,
                },
            });
            
            // التحقق من حالة الاستجابة
            if (response.data.status === 'faild' || response.data.status === 'failed') {
                return rejectWithValue(response.data.message || 'فشل في تغيير كلمة المرور');
            }
            
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.response?.data || 'فشل في تغيير كلمة المرور');
        }
    }
);

export const getCategories = createAsyncThunk(
    'storeAuth/getCategories',
    async (_, { rejectWithValue }) => {
        try {
            const currentLang = getCurrentLanguage();
            const response = await axios.get(`${BASE_URL}/categories/get_categories`, {
                headers: {
                    "Accept-Language": currentLang,
                    "lang": currentLang,
                },
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.response?.data || 'فشل في جلب الفئات');
        }
    }
);

export const getSubcategories = createAsyncThunk(
    'storeAuth/getSubcategories',
    async (categoryId, { rejectWithValue }) => {
        try {
            const currentLang = getCurrentLanguage();
            const response = await axios.get(`${BASE_URL}/categories/subcategories/${categoryId}`, {
                headers: {
                    "Accept-Language": currentLang,
                    "lang": currentLang,
                },
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.response?.data || 'فشل في جلب الفئات الفرعية');
        }
    }
);

const initialState = {
    user: null,
    token: null,
    loading: false,
    error: null,
    categories: [],
    subcategories: [],
    otpSent: false,
    otpVerified: false,
    passwordChanged: false,
    accountActivated: false,
    successMessage: null
};

const storeAuthSlice = createSlice({
    name: 'storeAuth',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccessMessage: (state) => {
            state.successMessage = null;
        },
        clearOtpStatus: (state) => {
            state.otpSent = false;
            state.otpVerified = false;
        },
        clearPasswordStatus: (state) => {
            state.passwordChanged = false;
        },
        clearActivationStatus: (state) => {
            state.accountActivated = false;
        },
        logout: (state) => {
            state.user = null;
            state.token = null;
            state.error = null;
            state.otpSent = false;
            state.otpVerified = false;
            state.passwordChanged = false;
            state.accountActivated = false;
        }
    },
    extraReducers: (builder) => {
        builder
            // Store Login
            .addCase(storeLogin.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(storeLogin.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload.result;
                state.token = action.payload.result?.token;
                state.error = null;
            })
            .addCase(storeLogin.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            
            // Store Register
            .addCase(storeRegister.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(storeRegister.fulfilled, (state, action) => {
                state.loading = false;
                state.otpSent = true;
                state.error = null;
                state.successMessage = action.payload.message || 'تم إنشاء الحساب بنجاح';
            })
            .addCase(storeRegister.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            
            // Store Reset (Send OTP)
            .addCase(storeReset.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(storeReset.fulfilled, (state, action) => {
                state.loading = false;
                state.otpSent = true;
                state.error = null;
            })
            .addCase(storeReset.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            
            // Store Check Code
            .addCase(storeCheckCode.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(storeCheckCode.fulfilled, (state, action) => {
                state.loading = false;
                state.otpVerified = true;
                state.error = null;
            })
            .addCase(storeCheckCode.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            
            // Store Change Password
            .addCase(storeChangePassword.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(storeChangePassword.fulfilled, (state, action) => {
                state.loading = false;
                state.passwordChanged = true;
                state.error = null;
            })
            .addCase(storeChangePassword.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            
            // Get Categories
            .addCase(getCategories.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getCategories.fulfilled, (state, action) => {
                state.loading = false;
                state.categories = action.payload.result || [];
                state.error = null;
            })
            .addCase(getCategories.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            
            // Get Subcategories
            .addCase(getSubcategories.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getSubcategories.fulfilled, (state, action) => {
                state.loading = false;
                state.subcategories = action.payload.result || [];
                state.error = null;
            })
            .addCase(getSubcategories.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    }
});

export const { 
    clearError, 
    clearSuccessMessage,
    clearOtpStatus, 
    clearPasswordStatus, 
    clearActivationStatus, 
    logout 
} = storeAuthSlice.actions;

export default storeAuthSlice.reducer;
